import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { printContent } from '../lib/exportUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  BrainCircuit, TrendingUp, DollarSign, PiggyBank, BarChart3,
  Send, RefreshCw, Sparkles, ShieldCheck, AlertTriangle,
  Lightbulb, Clock, Target, Loader2, Check, Star,
  BookOpen, Calculator, MessageSquare, MessageCircle, ChevronDown, ChevronRight,
  Building2, Briefcase, Code, HeartPulse, ShoppingBag, UtensilsCrossed,
  Globe, Factory, Users, Palette, Hammer, Printer, Download,
  ArrowRight, Info, X, Zap, Save, Pin, PinOff, Search, Trash2,
  FileText, Edit3, MoreVertical, FolderOpen, Plus, Wallet, Landmark,
  GraduationCap, Home, Heart, CircleDollarSign, Eye, BarChart2, Copy,
  Upload, ScanLine, CheckCircle2, XCircle, ChevronUp, Map, Wand2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HelpPanel } from '../components/ui/help-panel';
import { cfoBuckHelp } from '../lib/helpContent';

// Extracted components
import { TaxReturnResults, TaxAnalysisReport } from '../components/cfobuck/TaxResults';
import { ChatTab, SavedChatsTab } from '../components/cfobuck/ChatTab';
import { SimulatorTab, SavedPlansTab } from '../components/cfobuck/SimulatorTab';
import AgentTab from '../components/cfobuck/AgentTab';
import { IncomeProfileSection } from '../components/cfobuck/IncomeProfile';
import { QuickChatsTab } from '../components/cfobuck/QuickChatsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => { const n = typeof v === 'number' && !isNaN(v) ? v : (typeof v === 'string' ? parseFloat(v) : 0); return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(isNaN(n) ? 0 : n); };

// Reusable tooltip wrapper
const Tip = ({ children, text, side = 'top' }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);

// ─── SHARED CONFIG ──────────────────────────────────────────────────────────
const gradeConfig = {
  excellent: { color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500', label: 'Excellent' },
  good: { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500', label: 'Good' },
  fair: { color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500', label: 'Fair' },
  needs_attention: { color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500', label: 'Needs Attention' },
  critical: { color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500', label: 'Critical' },
};

const categoryIcons = { tax_planning: PiggyBank, cash_flow: DollarSign, expense_optimization: Target, revenue_growth: TrendingUp };
const categoryLabels = { tax_planning: 'Tax Planning', cash_flow: 'Cash Flow', expense_optimization: 'Expense Optimization', revenue_growth: 'Revenue Growth' };
const categoryColors = {
  tax_planning: 'border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10',
  cash_flow: 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10',
  expense_optimization: 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10',
  revenue_growth: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10',
};
const categoryIconColors = {
  tax_planning: 'text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30',
  cash_flow: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
  expense_optimization: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  revenue_growth: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30',
};

const industryIcons = {
  real_estate: Building2, construction: Hammer, tech_saas: Code, medical: HeartPulse,
  retail: ShoppingBag, restaurant: UtensilsCrossed, professional_services: Briefcase,
  ecommerce: Globe, manufacturing: Factory, consulting: Users, creative: Palette, other: Building2,
};

const playbookCategoryColors = {
  depreciation_assets: { border: 'border-blue-200 dark:border-blue-800', bg: 'bg-blue-50 dark:bg-blue-900/10', text: 'text-blue-600 dark:text-blue-400', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' },
  entity_income: { border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-900/10', text: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' },
  credits: { border: 'border-violet-200 dark:border-violet-800', bg: 'bg-violet-50 dark:bg-violet-900/10', text: 'text-violet-600 dark:text-violet-400', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400' },
  retirement_savings: { border: 'border-amber-200 dark:border-amber-800', bg: 'bg-amber-50 dark:bg-amber-900/10', text: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' },
  advanced_wealth: { border: 'border-rose-200 dark:border-rose-800', bg: 'bg-rose-50 dark:bg-rose-900/10', text: 'text-rose-600 dark:text-rose-400', badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400' },
};

const playbookCategoryLabels = {
  depreciation_assets: 'Depreciation & Assets', entity_income: 'Entity & Income', credits: 'Tax Credits',
  retirement_savings: 'Retirement & Savings', advanced_wealth: 'Advanced / Wealth',
};

const complexityConfig = {
  simple: { label: 'Simple', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  moderate: { label: 'Moderate', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  advanced: { label: 'Advanced', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
};

// ─── TAB CONFIG ─────────────────────────────────────────────────────────────
// Slimmed Feb-2026 IA refactor: Document Scanner + Tax Mapper moved to
// Accounting Hub. Saved Plans + Saved Chats merged into a single History tab.
const TABS = [
  { id: 'overview',  label: 'Overview',      icon: ShieldCheck },
  { id: 'playbook',  label: 'Tax Playbook',  icon: BookOpen },
  { id: 'simulator', label: 'Tax Simulator', icon: Calculator },
  { id: 'agent',     label: 'AI Skills',     icon: Wand2 },
  { id: 'chat',      label: 'Ask CFO Buck',  icon: MessageSquare },
  { id: 'history',   label: 'History',       icon: FolderOpen },
];

// ─── OVERVIEW TAB ───────────────────────────────────────────────────────────
const ScoreRing = ({ score, grade, size = 120 }) => {
  const cfg = gradeConfig[grade] || gradeConfig.fair;
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <Tip text={`Your overall financial health score is ${score}/100 (${cfg.label}). This combines profit margin, cash runway, debt ratio, and receivable speed into a single metric.`}>
      <div className="relative flex items-center justify-center cursor-help" style={{ width: size, height: size }} data-testid="health-score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted-foreground" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" strokeWidth="6" strokeLinecap="round" stroke="currentColor" className={cfg.color} strokeDasharray={c} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-3xl font-bold", cfg.color)} style={{ fontFamily: "'Outfit', sans-serif" }}>{score}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{cfg.label}</span>
      </div>
    </div>
    </Tip>
  );
};

const metricTooltips = {
  'Profit Margin': 'Net income as a percentage of revenue. Higher is better — above 20% is strong for most industries.',
  'Cash Runway': 'How many months your business can operate at current burn rate before running out of cash.',
  'Debt-to-Asset': 'Total liabilities divided by total assets. Lower is better — above 60% indicates high leverage.',
  'AR Collection': 'Average days to collect payment from customers. Lower is better — under 30 days is excellent.',
};

const MetricBar = ({ metric }) => {
  const cfg = gradeConfig[metric.grade] || gradeConfig.fair;
  const tip = metricTooltips[metric.label] || metric.label;
  // Cap Cash Runway display at 24+ months to avoid showing 999
  let displayValue = metric.value;
  if (metric.label === 'Cash Runway' && typeof metric.value === 'number' && metric.value >= 24) {
    displayValue = '24+';
  }
  return (
    <Tip text={tip} side="right">
      <div className="space-y-1.5 cursor-help" data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{metric.label}</span>
        <span className={cn("text-xs font-bold", cfg.color)}>{displayValue}{metric.unit}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-1000", cfg.bg)} style={{ width: `${metric.score}%` }} />
      </div>
    </div>
    </Tip>
  );
};

const InsightCard = ({ insight }) => {
  const Icon = categoryIcons[insight.category] || Lightbulb;
  const colorClass = categoryColors[insight.category] || 'border-border';
  const iconClass = categoryIconColors[insight.category] || 'text-muted-foreground bg-muted';
  const pColors = { high: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400', medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', low: 'bg-muted text-muted-foreground' };
  const priorityTooltips = { high: 'High priority — address this soon to avoid financial risk.', medium: 'Medium priority — plan to address this within the quarter.', low: 'Low priority — worth noting but not urgent.' };
  return (
    <div className={cn("rounded-xl border p-5 transition-shadow hover:shadow-md", colorClass)} data-testid={`insight-${insight.category}`}>
      <div className="flex items-start gap-3.5">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", iconClass)}><Icon className="w-4.5 h-4.5" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">{categoryLabels[insight.category] || insight.category}</span>
            <Tip text={priorityTooltips[insight.priority] || ''}>
              <span><Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 rounded-full border-0 cursor-help", pColors[insight.priority])}>{insight.priority}</Badge></span>
            </Tip>
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{insight.title}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">{insight.summary}</p>
          {insight.impact_estimate && (
            <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-border/50">
              <Sparkles className="w-3 h-3 text-violet-500" /><span className="text-[11px] font-medium text-violet-600 dark:text-violet-400">{insight.impact_estimate}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── TAX RETURN RESULTS COMPONENT ────────────────────────────────────────────
function OverviewTab({ health, insights, healthLoading, insightsLoading, cachedStatus, refreshInsights, fetchHealth, workspaceId, period, onPeriodChange }) {
  const periods = [
    { id: '1m', label: '1M' },
    { id: '3m', label: '3M' },
    { id: '6m', label: '6M' },
    { id: 'ytd', label: 'YTD' },
    { id: '12m', label: '12M' },
    { id: 'all', label: 'All' },
  ];
  const periodLabel = periods.find(p => p.id === period)?.label || 'YTD';

  return (
    <div className="space-y-8">
      {/* Period Selector */}
      <div className="flex items-center justify-between" data-testid="period-selector-bar">
        <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-0.5" data-testid="period-selector">
          {periods.map(p => (
            <Tip key={p.id} text={p.id === 'ytd' ? 'Year to date' : p.id === 'all' ? 'All available data' : `Last ${p.label}`}>
              <button
                onClick={() => onPeriodChange(p.id)}
                className={cn(
                  "px-3 py-1.5 text-xs font-semibold rounded-md transition-all",
                  period === p.id
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            </Tip>
          ))}
        </div>
        {health?.start_date && health?.end_date && (
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {new Date(health.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &ndash; {new Date(health.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-4 h-4 text-muted-foreground" /><span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Financial Health</span></div>
            {healthLoading ? (
              <div className="flex flex-col items-center py-6 space-y-4"><Skeleton className="w-[120px] h-[120px] rounded-full" /><Skeleton className="w-32 h-4" /></div>
            ) : health ? (
              <div className="flex flex-col items-center">
                <ScoreRing score={health.overall_score} grade={health.overall_grade} />
                <div className="w-full mt-6 space-y-3">{Object.values(health.metrics || {}).map((m, i) => <MetricBar key={i} metric={m} />)}</div>
              </div>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No financial data available</p>}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2 border-border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-5"><BarChart3 className="w-4 h-4 text-muted-foreground" /><span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Key Figures ({periodLabel})</span></div>
            {healthLoading ? (
              <div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
            ) : health?.summary ? (
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Revenue', value: health.summary.total_revenue, tip: 'Total income earned year-to-date before expenses.' },
                  { label: 'Net Income', value: health.summary.net_income, colored: true, tip: 'Revenue minus all expenses. Positive = profit, negative = loss.' },
                  { label: 'Cash Position', value: health.summary.total_cash, tip: 'Total cash and bank balances available right now.' },
                  { label: 'Monthly Burn', value: health.summary.monthly_burn, tip: 'Average monthly operating expenses. Lower burn = longer runway.' },
                ].map((item) => (
                  <Tip key={item.label} text={item.tip} side="bottom">
                    <div className="p-4 rounded-xl bg-muted border border-border cursor-help">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">{item.label}</p>
                    <p className={cn("text-2xl font-bold", item.colored ? (item.value >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400") : "text-foreground")} style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(item.value)}</p>
                    </div>
                  </Tip>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>}
          </CardContent>
        </Card>
      </div>
      {/* Proactive Insights */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-violet-500" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>Proactive Insights</h2>
          {insightsLoading && <Loader2 className="w-4 h-4 animate-spin text-violet-400" />}
          {cachedStatus && !insightsLoading && <Tip text="Insights are cached for 24 hours to reduce AI costs. Click 'Refresh Analysis' to force a fresh analysis."><span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full cursor-help"><Clock className="w-3 h-3 inline mr-1" />{cachedStatus}</span></Tip>}
        </div>
        {insightsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
        ) : insights.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insights-grid">{insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}</div>
        ) : (
          <Card className="border-dashed"><CardContent className="py-12 text-center"><Lightbulb className="w-8 h-8 mx-auto text-muted-foreground/50 mb-3" /><p className="text-sm text-muted-foreground">Add financial data to unlock CFO insights</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}

// ─── TAX PLAYBOOK TAB ───────────────────────────────────────────────────────
function StrategyCard({ strategy, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const cc = playbookCategoryColors[strategy.category] || playbookCategoryColors.entity_income;
  const cx = complexityConfig[strategy.complexity] || complexityConfig.moderate;

  return (
    <div className={cn("rounded-xl border p-5 transition-all hover:shadow-md", cc.border, strategy.user_status === 'used' ? 'ring-2 ring-emerald-500/30' : '')} data-testid={`strategy-${strategy.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 border-0 rounded-full", cc.badge)}>{playbookCategoryLabels[strategy.category]}</Badge>
            <Tip text={strategy.complexity === 'simple' ? 'You can likely implement this yourself with minimal guidance.' : strategy.complexity === 'moderate' ? 'May require some professional setup or review from a tax professional.' : 'Requires specialized professionals (attorneys, actuaries, or CPAs) to implement correctly.'}>
              <span><Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 border-0 rounded-full cursor-help", cx.color)}>{cx.label}</Badge></span>
            </Tip>
            {strategy.cpa_required && <Tip text="This strategy involves complex tax law. We recommend working with a CPA or tax attorney to implement it correctly."><span><Badge variant="outline" className="text-[9px] px-1.5 py-0 border-0 rounded-full bg-muted text-muted-foreground cursor-help">CPA Advised</Badge></span></Tip>}
            {strategy.user_status === 'used' && <Tip text="You've marked this strategy as used. It's tracked in your tax history."><span><Badge className="text-[9px] px-1.5 py-0 bg-emerald-500 text-white cursor-help"><Check className="w-2.5 h-2.5 mr-0.5" />Used</Badge></span></Tip>}
            {strategy.user_status === 'interested' && <Tip text="You've flagged this for further review."><span><Badge className="text-[9px] px-1.5 py-0 bg-amber-500 text-white cursor-help"><Star className="w-2.5 h-2.5 mr-0.5" />Interested</Badge></span></Tip>}
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{strategy.name}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">{strategy.summary}</p>
          <div className="flex items-center gap-3">
            <Tip text="Estimated annual tax savings range based on typical business profiles. Your actual savings depend on your specific financials.">
              <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 cursor-help"><DollarSign className="w-3 h-3 inline" /> {strategy.estimated_savings_range}</span>
            </Tip>
            {strategy.relevance_score >= 0.8 && <Tip text="This strategy is especially relevant for your selected industry based on common deduction patterns."><span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 cursor-help"><Zap className="w-3 h-3 inline" /> High Relevance</span></Tip>}
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground p-1">
          <ChevronDown className={cn("w-4 h-4 transition-transform", expanded && "rotate-180")} />
        </button>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">{strategy.details}</p>
          {strategy.action_steps?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2">Action Steps</p>
              <ol className="space-y-1.5">
                {strategy.action_steps.map((step, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-2">
                    <span className="text-[10px] font-bold text-muted-foreground mt-0.5">{i+1}.</span>{step}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" variant={strategy.user_status === 'used' ? 'default' : 'outline'}
              className={strategy.user_status === 'used' ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs' : 'text-xs'}
              onClick={() => onStatusChange(strategy.id, strategy.user_status === 'used' ? 'none' : 'used')}
              data-testid={`strategy-used-${strategy.id}`}>
              <Check className="w-3 h-3 mr-1" />{strategy.user_status === 'used' ? 'Marked as Used' : "I've Used This"}
            </Button>
            <Button size="sm" variant={strategy.user_status === 'interested' ? 'default' : 'outline'}
              className={strategy.user_status === 'interested' ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs' : 'text-xs'}
              onClick={() => onStatusChange(strategy.id, strategy.user_status === 'interested' ? 'none' : 'interested')}
              data-testid={`strategy-interested-${strategy.id}`}>
              <Star className="w-3 h-3 mr-1" />{strategy.user_status === 'interested' ? 'Interested' : 'Interested'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybookTab({ workspaceId }) {
  const [strategies, setStrategies] = useState([]);
  const [industry, setIndustry] = useState('other');
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [usedCount, setUsedCount] = useState(0);
  const [interestedCount, setInterestedCount] = useState(0);

  const fetchStrategies = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [stratRes, indRes] = await Promise.all([
        fetch(`${API_URL}/api/workspaces/${workspaceId}/tax-playbook/strategies`),
        fetch(`${API_URL}/api/tax-playbook/industries`),
      ]);
      if (stratRes.ok) {
        const d = await stratRes.json();
        setStrategies(d.strategies || []);
        setIndustry(d.industry || 'other');
        setUsedCount(d.used_count || 0);
        setInterestedCount(d.interested_count || 0);
      }
      if (indRes.ok) {
        const d = await indRes.json();
        setIndustries(d.industries || []);
      }
    } catch (e) { console.error('Playbook fetch error:', e); }
    finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);

  const handleIndustryChange = async (newIndustry) => {
    setIndustry(newIndustry);
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-playbook/industry`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ industry: newIndustry }),
    });
    fetchStrategies();
  };

  const handleStatusChange = async (strategyId, status) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-playbook/strategies/${strategyId}/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, used_year: new Date().getFullYear() }),
    });
    fetchStrategies();
  };

  const filtered = filterCategory === 'all' ? strategies : strategies.filter(s => s.category === filterCategory);
  const IndustryIcon = industryIcons[industry] || Building2;

  return (
    <div className="space-y-6">
      {/* Industry Selector */}
      <Card className="border-border">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <IndustryIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Your Industry</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span><Check className="w-3 h-3 inline text-emerald-500" /> {usedCount} used</span>
              <span><Star className="w-3 h-3 inline text-amber-500" /> {interestedCount} interested</span>
              <span>{strategies.length} strategies</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="industry-selector">
            {industries.map((ind) => {
              const Icon = industryIcons[ind.id] || Building2;
              return (
                <button key={ind.id} onClick={() => handleIndustryChange(ind.id)}
                  className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                    industry === ind.id
                      ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                      : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                  )} data-testid={`industry-${ind.id}`}>
                  <Icon className="w-3 h-3" />{ind.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2" data-testid="category-filter">
        <button onClick={() => setFilterCategory('all')} className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-all", filterCategory === 'all' ? "bg-foreground text-background border-transparent" : "border-border text-muted-foreground")}>All ({strategies.length})</button>
        {Object.entries(playbookCategoryLabels).map(([id, label]) => {
          const count = strategies.filter(s => s.category === id).length;
          return (
            <button key={id} onClick={() => setFilterCategory(id)} className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-all", filterCategory === id ? "bg-foreground text-background border-transparent" : "border-border text-muted-foreground")}>
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Strategy Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="strategies-grid">
          {filtered.map((s) => <StrategyCard key={s.id} strategy={s} onStatusChange={handleStatusChange} />)}
        </div>
      )}
    </div>
  );
}

// ─── TAX SIMULATOR TAB ──────────────────────────────────────────────────────
export default function CFOBuck() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [historySubTab, setHistorySubTab] = useState('chats');
  const [resumeConversation, setResumeConversation] = useState(null);
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);

  const [health, setHealth] = useState(null);
  const [insights, setInsights] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [cachedStatus, setCachedStatus] = useState(null);
  const [period, setPeriod] = useState('ytd');
  const [pendingTaxAnalysis, setPendingTaxAnalysis] = useState(null);

  const wsId = currentWorkspace?.workspace_id;

  // Deep-link from All Conversations: ?tab=history&resume={conv_id} loads
  // the saved CFO Buck chat. ?tab=simulator&plan={plan_id} pre-selects plan.
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId && wsId && activeTab === 'history') {
      (async () => {
        try {
          const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/cfo-buck/conversations/${resumeId}`);
          if (res.ok) {
            setResumeConversation(await res.json());
            setActiveTab('chat');
          }
        } catch (e) { console.error(e); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, searchParams.get('resume')]);

  // Pick up tax-analysis prompts handed off from the Statement Importer
  // (the Scanner lives there now after the Feb-2026 IA refactor).
  useEffect(() => {
    try {
      const stashed = sessionStorage.getItem('cfo_pending_tax_analysis');
      if (stashed) {
        setPendingTaxAnalysis(stashed);
        setActiveTab('chat');
        sessionStorage.removeItem('cfo_pending_tax_analysis');
      }
    } catch { /* ignore */ }
  }, []);

  const fetchHealth = useCallback(async (p) => {
    if (!wsId) return;
    const usePeriod = p || period;
    setHealthLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/cfo-buck/health?period=${usePeriod}`);
      if (res.ok) setHealth(await res.json());
    } catch (e) { console.error(e); }
    finally { setHealthLoading(false); }
  }, [wsId, period]);

  const fetchInsights = useCallback(async (p) => {
    if (!wsId) return;
    const usePeriod = p || period;
    setInsightsLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/cfo-buck/insights?period=${usePeriod}`);
      if (res.ok) { const d = await res.json(); setInsights(d.insights || []); setCachedStatus(d.cached ? 'Cached until refresh' : 'Fresh analysis'); }
    } catch (e) { console.error(e); }
    finally { setInsightsLoading(false); }
  }, [wsId, period]);

  const refreshInsights = useCallback(async (p) => {
    if (!wsId) return;
    const usePeriod = p || period;
    setInsightsLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/cfo-buck/refresh-insights?period=${usePeriod}`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); setInsights(d.insights || []); setCachedStatus('Fresh analysis'); }
    } catch (e) { console.error(e); }
    finally { setInsightsLoading(false); }
  }, [wsId, period]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    fetchHealth(newPeriod);
    fetchInsights(newPeriod);
  };

  useEffect(() => { fetchHealth(); fetchInsights(); }, [fetchHealth, fetchInsights]);

  // (handleAnalyzeTaxReturn is no longer needed here — the Statement Importer
  // owns that flow and hands the prompt back via sessionStorage above.)

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6" data-testid="cfo-buck-page">
      {/* Page Header */}
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <BrainCircuit className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="cfo-buck-heading">CFO Buck</h1>
            <p className="text-sm text-muted-foreground">AI Financial Officer & Tax Strategist</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'overview' && (
            <Button variant="outline" size="sm" onClick={() => { fetchHealth(); refreshInsights(); }} className="text-muted-foreground" data-testid="refresh-insights-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh Analysis
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/statement-importer?docType=tax_return')}
            className="gap-1.5"
            data-testid="analyze-tax-return-btn"
          >
            <ScanLine className="w-3.5 h-3.5" />
            Analyze a tax return
          </Button>
          <HelpPanel {...cfoBuckHelp} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="cfo-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={cn("flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px",
              activeTab === tab.id
                ? "border-violet-500 text-violet-600 dark:text-violet-400"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            )} data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab health={health} insights={insights} healthLoading={healthLoading} insightsLoading={insightsLoading} cachedStatus={cachedStatus} refreshInsights={refreshInsights} fetchHealth={fetchHealth} workspaceId={wsId} period={period} onPeriodChange={handlePeriodChange} />
      )}
      {activeTab === 'playbook' && <PlaybookTab workspaceId={wsId} />}
      {activeTab === 'simulator' && <SimulatorTab workspaceId={wsId} />}
      {activeTab === 'agent' && <AgentTab workspaceId={wsId} />}
      {activeTab === 'chat' && <ChatTab workspaceId={wsId} onSaved={() => setSavedRefreshKey(k => k + 1)} resumeConversation={resumeConversation} setResumeConversation={setResumeConversation} pendingTaxAnalysis={pendingTaxAnalysis} setPendingTaxAnalysis={setPendingTaxAnalysis} />}
      {activeTab === 'history' && (
        <div className="space-y-4" data-testid="history-tab">
          {/* Sub-tabs for chats vs plans vs quick chats */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setHistorySubTab('chats')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                historySubTab === 'chats'
                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30"
                  : "text-muted-foreground hover:text-foreground border border-transparent"
              )}
              data-testid="history-subtab-chats"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Saved Chats
            </button>
            <button
              onClick={() => setHistorySubTab('quick-chats')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                historySubTab === 'quick-chats'
                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30"
                  : "text-muted-foreground hover:text-foreground border border-transparent"
              )}
              data-testid="history-subtab-quick-chats"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Quick Chats
            </button>
            <button
              onClick={() => setHistorySubTab('plans')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                historySubTab === 'plans'
                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30"
                  : "text-muted-foreground hover:text-foreground border border-transparent"
              )}
              data-testid="history-subtab-plans"
            >
              <Calculator className="w-3.5 h-3.5" />
              Saved Tax Plans
            </button>
          </div>

          {historySubTab === 'chats' && (
            <SavedChatsTab key={savedRefreshKey} workspaceId={wsId} onResume={(conv) => {
              const fetchFull = async () => {
                try {
                  const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/cfo-buck/conversations/${conv.conversation_id}`);
                  if (res.ok) { setResumeConversation(await res.json()); setActiveTab('chat'); }
                } catch (e) { console.error(e); }
              };
              fetchFull();
            }} />
          )}
          {historySubTab === 'quick-chats' && <QuickChatsTab />}
          {historySubTab === 'plans' && <SavedPlansTab workspaceId={wsId} />}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
